<script setup>
  import {ref, watch} from "vue";

  import ComponentBreadcrumb from "@components/Breadcrumb/Breadcrumb.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentFilterFlags from "@components/Filter/FilterFlags.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentPagination from "@components/Pagination/Pagination.vue";
  import {router, useForm, usePage} from "@inertiajs/vue3";
  import ContactsContactHead from "@pages/Contact/Components/ContactHead.vue";
  import ContactsContactRow from "@pages/Contact/Components/ContactRow.vue";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "PagesContactsIndex",
  });

  defineProps({
    contacts: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  });

  const page = usePage();

  const refDeleteDialog = ref(null);
  const selectedId = ref(null);

  const availableFlags = {
    "no-patients": "Keinem Patienten zugeordnet",
  };

  let form = useForm({
    order: page.props?.filters?.order || "lastname",
    filter: typeof page.props?.filters?.filter !== "function" ? page.props?.filters?.filter : null,
    q: {
      search: page.props?.filters?.q?.search || null,
    },
    flags: page.props?.filters?.flags || [],
  });

  watch(
    () => form.q.search,
    debounce(function () {
      submitFilter();
    }, 500),
  );

  watch(
    () => form.flags,
    debounce(function () {
      submitFilter();
    }, 500),
    {deep: true}, // required when watching arrays
  );

  watch(
    () => form.order,
    debounce(function () {
      submitFilter();
    }, 500),
  );

  function submitFilter() {
    form.get(route("contacts.index"), {preserveState: true, preserveScroll: true, replace: true});
  }

  const openDialog = (id, dialogType) => {
    selectedId.value = id;

    if (dialogType === "delete") refDeleteDialog.value.open();
  };

  const closeDialog = (dialogType) => {
    selectedId.value = null;

    if (dialogType === "delete") refDeleteDialog.value.close();
  };

  const deleteRow = () => {
    const contactId = selectedId.value;

    router.delete(route("contacts.destroy", {contact: contactId}), {
      preserveScroll: true,
      optimistic: (pageProperties) => ({
        contacts: {
          ...pageProperties.contacts,
          data: pageProperties.contacts.data.filter((c) => c.id !== contactId),
        },
      }),
    });
  };
</script>

<template>
  <ComponentBreadcrumb class="mt-2" :links="[{label: 'Kontakte'}]" />

  <section class="mt-6">
    <div class="bg-white p-4 shadow-sm">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-semibold">Kontakte</h2>

        <MedButton
          variant="primary"
          class="self-end"
          as="inertia"
          :href="$route('contacts.create')"
          :data-test="getTestId('create-new-contact-button')"
        >
          Neuer Kontakt
        </MedButton>
      </div>

      <!-- filter :: start -->
      <div class="mt-2">
        <form :data-test="getTestId('form')" @submit.prevent="submitFilter">
          <div class="flex items-baseline justify-between space-x-8">
            <ComponentInput
              v-model="form.q.search"
              autocomplete="off"
              class="max-w-52"
              placeholder="Suchen"
              :test-id="getTestId('form-search')"
            >
              <template #icon>
                <ComponentIcon class="text-xl text-gray-600">search</ComponentIcon>
              </template>
            </ComponentInput>

            <ComponentFilterFlags
              v-model="form.flags"
              :available-flags="availableFlags"
              data-test="contact"
              :test-id="getTestId()"
            />
          </div>
        </form>
      </div>
      <!-- filter :: end -->

      <!-- table listing :: start -->
      <article class="mt-4">
        <table v-if="contacts.data.length > 0" class="w-full table-auto">
          <thead>
            <ContactsContactHead v-model="form.order" :test-id="getTestId()" />
          </thead>

          <tbody>
            <template v-if="form.processing">
              <tr>
                <td colspan="12">
                  <div class="grid min-h-32 w-full place-content-center">
                    <MedSpinner size="xl" :data-test="getTestId('contact-index-spinner')" />
                  </div>
                </td>
              </tr>
            </template>

            <template v-else>
              <ContactsContactRow
                v-for="(contact, index) in contacts.data"
                :key="contact.id"
                :contact="contact"
                :test-id="getTestId(`${index}`)"
                @open:delete-contact="openDialog(contact.id, 'delete')"
              />
            </template>
          </tbody>
        </table>

        <template v-else>
          <ComponentEmptyState v-if="form.q.search || form.flags.length > 0" :test-id="getTestId('no-results')">
            Für die aktuelle Suche / Filter wurden keine Kontakte gefunden.
          </ComponentEmptyState>

          <ComponentEmptyState v-else class="mr-10 max-w-152" :test-id="getTestId()">
            <div class="flex items-start">
              <div class="space-y-4">
                <p>
                  Legen Sie über den Button
                  <span class="font-semibold">„Neuer Kontakt“</span>
                  Adressen von Ärzten, Pflegediensten oder Heimen an, mit denen Sie in Kontakt stehen.
                </p>
              </div>

              <div class="relative -top-6 fill-blue-900">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 100 100"
                  class="size-32 rotate-90"
                >
                  <path
                    d="M5.78 13.88c3.89-.42 7.89.35 11.76.77l11.91 1.32c1.31.14 2.82 3.76 1.07 3.78-.29 0-.57 0-.86.01-.2 0-.4-.06-.6-.16l-10.27-1.14c-2.27-.25-4.58-.62-6.89-.79 26.97 9.44 53.72 22.63 71.31 45.91 4.78 6.33 8.75 13.24 11.63 20.64.26.68.34 1.88-.6 2.03-.93.15-1.76-1.07-2.04-1.78-10.34-26.5-34.56-44.05-59.69-55.27-6.59-2.94-13.34-5.51-20.16-7.89 1.72 1.81 3.43 3.62 5.15 5.42.55.58 1.08 1.93.52 2.65-.61.8-1.59 0-2.06-.49-2.16-2.28-4.32-4.55-6.48-6.83-1.87-1.97-3.96-3.87-4.45-6.67-.1-.57.01-1.43.75-1.51z"
                  />
                </svg>
              </div>
            </div>
          </ComponentEmptyState>
        </template>

        <!-- Paginator -->
        <ComponentPagination v-if="!form.processing && contacts.meta" :links="contacts.meta.links" />
      </article>
      <!-- table listing :: end -->
    </div>
  </section>

  <ComponentDialog
    ref="refDeleteDialog"
    dialog-title="Sind Sie sicher?"
    dialog-content="Die ausgewählte Zeile wird gelöscht."
    primary-label="OK"
    :test-id="getTestId('delete')"
    @primary-button-clicked="deleteRow"
    @cancel-button-clicked="closeDialog('delete')"
  />
</template>
